import { use_dispatch } from '@/hooks/_base/use_dispatch';
import { use_effect } from '@/hooks/_base/use_effect';
import { use_slice_search, use_slice_ui } from '@/hooks/store/use_slices';
import { ButtonComponentActionEmpty } from '@/pages/_base/buttons/ButtonComponentActionEmpty';
import { ButtonWorkspaceChildEntityDelete } from '@/pages/_base/buttons/ButtonWorkspaceChildEntityDelete';
import { ButtonWorkspaceChildEntityShare } from '@/pages/_base/buttons/ButtonWorkspaceChildEntityShare';
import { LOCAL__ui_set_boolean } from '@/state/ui/_base/LOCAL__ui_set_boolean';
import { set_current_search } from '@/utils/utils_client_workspace_child_entity_viewers';
import { ENUM__workspace_entity_feature } from '@shared/dto/+workspace/workspace_entity/_enums/ENUM__workspace_entity_feature';
import { DTOSearchSettings } from '@shared/dto/search/dto_search_settings';
import { format_timestamp_YY_MM_DD_HH_mm_ss } from '@shared/utils/utils_time';
import { useState } from 'react';
import { SButtonHistoryListItemPin } from './_buttons/SButtonHistoryListItemPin';
import { SButtonHistoryListItemUpdateName } from './_buttons/SButtonHistoryListItemUpdateName';
import { SButtonHistoryListItemUpdateVisibility } from './_buttons/SButtonHistoryListItemUpdateVisibility';
import { SInputSearchItemName } from './SInputSearchItemName';

interface Props {
  item: DTOSearchSettings;
}

export function SSearchHistoryListItem({ item }: Props) {
  const dispatch = use_dispatch();

  const [isMouseOver, setIsMouseOver] = useState(false);
  const [isShowInputUpdateName, setIsShowInputUpdateName] = useState(false);
  const { global_is_mobile, global_is_desktop } = use_slice_ui(['global_is_desktop', 'global_is_mobile']);

  const { search_current } = use_slice_search(['search_current']);

  const [isButtonDeleteClicked, setIsButtonDeleteClicked] = useState(false);

  /*
   * EFFECTS
   */
  use_effect(() => {
    if (isButtonDeleteClicked) return;

    setIsMouseOver(false);
  }, [isButtonDeleteClicked]);

  /*
   * EVENTS
   */
  async function onClick() {
    if (is_selected()) {
      //open search
      dispatch(LOCAL__ui_set_boolean({ value: false, key: 'page_search_show_history' }));
    } else {
      await set_current_search(item._id);
    }
  }

  function onMouseLeaveItem() {
    if (isButtonDeleteClicked) return;

    setIsMouseOver(false);
  }

  /*
   * METHODS
   */
  function getClassName() {
    let out = '';

    if (isMouseOver || is_selected()) {
      out = 'list-item-selected';
    } else {
      out = 'bg-component hover:list-item-selected';
    }

    return out;
  }

  function is_selected() {
    return search_current._id == item._id;
  }

  /*
   * RENDERS
   */

  function render_button_share() {
    let out = <ButtonComponentActionEmpty />;

    if (isMouseOver || item.is_shared || global_is_mobile) {
      out = (
        <ButtonWorkspaceChildEntityShare
          item={item}
          isSelected={is_selected() || (isMouseOver && global_is_desktop)}
          required_permission={ENUM__workspace_entity_feature.ENTITY_SEARCH_SHARE}
        />
      );
    }

    return out;
  }

  function render_button_update() {
    let out = <ButtonComponentActionEmpty />;

    if (isMouseOver || global_is_mobile) {
      out = <SButtonHistoryListItemUpdateName {...{ setIsShowInputUpdateName, item }} />;
    }

    return out;
  }

  function render_button_change_visibility() {
    let out = <ButtonComponentActionEmpty />;

    if (isMouseOver || global_is_mobile) {
      out = <SButtonHistoryListItemUpdateVisibility item={item} />;
    }

    return out;
  }

  function render_button_delete() {
    let out = <ButtonComponentActionEmpty />;

    if (isMouseOver || global_is_mobile) {
      out = (
        <ButtonWorkspaceChildEntityDelete
          setIsButtonDeleteClicked={setIsButtonDeleteClicked}
          entity={item}
          required_permission={ENUM__workspace_entity_feature.ENTITY_SEARCH_DELETE}
          isSelected={is_selected()}
        />
      );
    }

    return out;
  }

  function renderButtonChangeVisibility() {
    return (
      <>
        {render_button_update()}
        {render_button_change_visibility()}
        {render_button_share()}
        {render_button_delete()}
      </>
    );
  }

  function renderButtonPin() {
    let out = <></>;

    if (item.is_pinned || isMouseOver || global_is_mobile) {
      out = <SButtonHistoryListItemPin item={item} />;
    }

    return out;
  }

  function render_name() {
    return item.name.trim().length > 0 ? item.name : format_timestamp_YY_MM_DD_HH_mm_ss(item.created_at);
  }

  if (isShowInputUpdateName) return <SInputSearchItemName item={item} setIsShowInputUpdateName={setIsShowInputUpdateName} />;

  return (
    <div
      className={`list-item-wrapper hover-animation text-sm text-left pl-1 pr-1 ${getClassName()}`}
      {...{ onClick }}
      onMouseEnter={() => setIsMouseOver(true)}
      onMouseLeave={() => onMouseLeaveItem()}
    >
      <div className="list-item-inner">
        <div className="flex flex-row items-center justify-start">
          <span className="w-10 ">{renderButtonPin()}</span>
          <span className={`    ${item.is_hidden ? 'opacity-50' : ''}`}>{render_name()}</span>
        </div>
        <span className="flex items-center justify-start gap-1">{renderButtonChangeVisibility()}</span>
      </div>
    </div>
  );
}
